import { CarConfiMarketConfiguration } from '~/app/market-config/models/car-confi-market-configuration';
import { groupItemsByGroupAndFamily } from '~/app/utils/group-items';
import GetUrlFromImageSet, {
	GetMediaArrayFromImageSet,
	GetMediaFromImageSet,
	GetModifiedItemsWithItemNameFromMedia,
	GetModifiedItemWithNameFromMedia,
	GetUrlArrayFromImageSet,
} from '~/graphql/utils/get-from-image-set';
import { modifyImageResolution } from '~/graphql/utils/modify-image-url';
import { TECHNOLOGY_PACKAGES } from '~/utils/family-constants';
import {
	AccessoriesQuery,
	CatalogQuery,
	ConfiguredCarFeatureFragment,
	ConfiguredCarPackageFragment,
	PriceFragment,
	Status,
} from '../__generated__/graphql';
import { FeatureCategory, FeatureOrPackage } from '../types';

export function mapAccessories(response: AccessoriesQuery, config: CarConfiMarketConfiguration) {
	const accessoriesInclusive = mapAccessoriesToCatalogItem(response.configuredCar.accessoriesInclusive);
	const accessoriesExclusive = mapAccessoriesToCatalogItem(response.configuredCar.accessoriesExclusive);
	const finalExclusives = groupItemsByGroupAndFamily(accessoriesExclusive).filter(
		(itm) => !itm.id.includes('Weitere'),
	);
	finalExclusives.forEach((exclusiveItem) => {
		exclusiveItem.families.forEach((family) => {
			family.items = family.items.filter(
				(item) => item.status?.available || (item.family.id === TECHNOLOGY_PACKAGES && item.status?.standard),
			);
		});
	});

	return {
		accessoriesInclusive: groupItemsByGroupAndFamily(accessoriesInclusive),
		accessoriesExclusive: finalExclusives,
		equipmentHighlights: findEquipmentHighlights(response, config).map((item) => mapAccessoryItem(item)),
	};
}

export type MappedAccessories = ReturnType<typeof mapAccessories>;

export type Accessories = ReturnType<typeof mapAccessories>['accessoriesInclusive'];

function mapAccessoriesToCatalogItem(
	data: NonNullable<AccessoriesQuery['configuredCar']['accessoriesInclusive']> | null | undefined,
) {
	const featuresAndPackages = [...array(data?.features.data), ...array(data?.packagesDetails.data)];
	return featuresAndPackages.map((item) => mapAccessoryItem(item));
}

function mapAccessoryItem(item: FeatureOrPackage) {
	return {
		...getDefaultProperties('accessory', {
			...item,
			name: item.name || '',
			pr7: item.pr7,
			status: item.status,
		}),
		...getPrices(item),
		imageUrl:
			GetUrlFromImageSet(item.imageSets, 'teaser_image') || GetUrlFromImageSet(item.imageSets, 'hd_thumbnail'),
		galleryImages: GetUrlArrayFromImageSet(item.imageSets, 'hd_image') || [''],
		galleryMedia: GetModifiedItemsWithItemNameFromMedia(
			GetMediaArrayFromImageSet(item.imageSets, 'hd_image') || [],
			item.name || '',
		),
		family: {
			id: item.family?.id,
			name: item.family?.name,
			__typename: item.family?.__typename,
		},
		group: {
			id: item.group?.id,
			name: item.group?.name,
			__typename: item.group?.__typename,
		},
		pr3: '',
		featureType: item.featureType,
		__typename: item.__typename,
	};
}

function findEquipmentHighlights(response: AccessoriesQuery, config: CarConfiMarketConfiguration) {
	if (!config.combinations.find((a) => a === 'equipmentHighlights')) return [];

	const items = [
		...array(response.configuredCar.accessoriesExclusive?.features.data),
		...array(response.configuredCar.accessoriesExclusive?.packagesDetails.data),
		...array(response.configuredCar.accessoriesInclusive?.features.data),
		...array(response.configuredCar.accessoriesInclusive?.packagesDetails.data),
	];

	return items.filter((item) => item.family.name === 'Single Items');
}

export type Accessory = ReturnType<typeof mapAccessoriesToCatalogItem>[0];

export function mapCatalog(response: CatalogQuery, config: CarConfiMarketConfiguration) {
	const catalog = response.configuredCar;
	if (!catalog) return undefined;

	const rims = [...array(catalog.rims?.features.data), ...array(catalog.rims?.packagesDetails.data)];
	const steeringWheels = [
		...array(catalog.steeringWheels?.features.data),
		...array(catalog.steeringWheels?.packagesDetails.data),
	];
	const seats = [...array(catalog.seats?.features.data), ...array(catalog.seats?.packagesDetails.data)];
	const interiorTiles = [
		...array(catalog.interiorTiles?.features.data),
		...array(catalog.interiorTiles?.packagesDetails.data),
	];
	const headlights = [...array(catalog.lights?.features.data), ...array(catalog.lights?.packagesDetails.data)];
	const exteriorColor = [
		...array(catalog.exteriorColors?.features.data),
		...array(catalog.exteriorColors?.packagesDetails.data),
	];

	const bodyColor = [...array(catalog.bodyColors?.features.data), ...array(catalog.bodyColors?.packagesDetails.data)];
	const roofColor = [...array(catalog.roofColors?.features.data), ...array(catalog.roofColors?.packagesDetails.data)];
	const exteriorPaintTypes = [
		...array(catalog.exteriorPaintTypes?.features.data),
		...array(catalog.exteriorPaintTypes?.packagesDetails.data),
	];

	return {
		rims: rims.map((item) => ({
			...getDefaultProperties('rims', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			wheelImageUrl: GetUrlFromImageSet(item.imageSets, 'wheel_image_small'),
			sizeCategory: item.subFamily?.name,
			galleryMedia: GetModifiedItemWithNameFromMedia(
				GetMediaFromImageSet(item.imageSets, 'wheel_image_small') || undefined,
				item.name || '',
			),
		})),
		steeringWheels: steeringWheels.map((item) => ({
			...getDefaultProperties('steeringWheel', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			stearingWheelImageSmallUrl: GetUrlFromImageSet(item.imageSets, 'steering_wheel_image_small') || '',
			galleryMedia: GetModifiedItemWithNameFromMedia(
				GetMediaFromImageSet(item.imageSets, 'steering_wheel_image_small') || undefined,
				item.name || '',
			),
		})),
		seats: seats.map((item) => ({
			...getDefaultProperties('seat', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			seatImageSmallUrl: GetUrlFromImageSet(item.imageSets, 'seat_image_small'),
			galleryMedia: GetModifiedItemWithNameFromMedia(
				GetMediaFromImageSet(item.imageSets, 'seat_image_small') || undefined,
				item.name || '',
			),
		})),
		interiorTiles: interiorTiles.map((item) => {
			return {
				...getDefaultProperties('interiorTile', {
					...item,
					name: item.name || '',
				}),
				...getPrices(item),
				teaserImageUrl: GetUrlFromImageSet(item.imageSets, 'teaser_image'),
				interiorTileButtonUrl: GetUrlFromImageSet(item.imageSets, 'interior_tile_button'),
				galleryImgUrl: GetUrlFromImageSet(item.imageSets, 'interior_tile_hd_square') || '',
				galleryMedia: GetModifiedItemWithNameFromMedia(
					GetMediaFromImageSet(item.imageSets, 'interior_tile_hd_square') || undefined,
					item.name || '',
				),
				family: {
					id: item.family?.id,
					name: item.family?.name,
				},
				group: {
					id: item.group?.id,
					name: item.group?.name,
				},
			};
		}),
		headlights: headlights.map((item) => ({
			...getDefaultProperties('headlight', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			teaserImageUrl: GetUrlFromImageSet(item.imageSets, 'teaser_image'),
			galleryImages: GetUrlArrayFromImageSet(item.imageSets, 'hd_image') || [''],
			galleryMedia: GetModifiedItemsWithItemNameFromMedia(
				GetMediaArrayFromImageSet(item.imageSets, 'hd_image'),
				item.name || '',
			),
		})),
		exteriorColor: exteriorColor.map((item) => ({
			...getDefaultProperties('bodyPaint', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			familyName: item.family?.name,
			familyId: item.family?.id,
			imgUrl: modifyImageResolution(GetUrlFromImageSet(item.imageSets, 'outer_tile_button') || '', '100'),
			galleryImgUrl: GetUrlFromImageSet(item.imageSets, 'outer_tile_hd') || '',
			galleryMedia: GetModifiedItemWithNameFromMedia(
				GetMediaFromImageSet(item.imageSets, 'outer_tile_hd_square') || null,
				item.name || '',
			),
		})),
		bodyColor: bodyColor.map((item) => ({
			...getDefaultProperties('bodyPaint', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			familyName: item.family?.name,
			familyId: item.family?.id,
			imgUrl: modifyImageResolution(GetUrlFromImageSet(item.imageSets, 'outer_tile_button') || '', '100'),
			galleryImgUrl: GetUrlFromImageSet(item.imageSets, 'outer_tile_hd') || '',
			galleryMedia: GetModifiedItemWithNameFromMedia(
				GetMediaFromImageSet(item.imageSets, 'outer_tile_hd') || null,
				item.name || '',
			),
		})),
		roofColor: roofColor.map((item) => ({
			...getDefaultProperties('bodyPaint', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			familyName: item.family?.name,
			familyId: item.family?.id,
			imgUrl: modifyImageResolution(GetUrlFromImageSet(item.imageSets, 'folding_tile_button') || '', '100'),
			galleryImgUrl: GetUrlFromImageSet(item.imageSets, 'folding_tile_hd') || '',
			galleryMedia: GetModifiedItemWithNameFromMedia(
				GetMediaFromImageSet(item.imageSets, 'folding_tile_hd') || null,
				item.name || '',
			),
		})),
		exteriorPaintTypes: exteriorPaintTypes.map((item) => ({
			...getDefaultProperties('exteriorPaintType', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			teaserImageUrl: GetUrlFromImageSet(item.imageSets, 'teaser_image'),
			galleryMedia: GetModifiedItemWithNameFromMedia(
				GetMediaFromImageSet(item.imageSets, 'teaser_image'),
				item.name || '',
			),
		})),
		packages: mapPackages(response, config),
	};
}

function mapPackages(response: CatalogQuery, config: CarConfiMarketConfiguration) {
	let visiblePackages = response.configuredCar.packages?.packagesDetails.data;
	let features = response.configuredCar.packages?.features.data;

	const PACKAGE_IDENTIFIER = 'Packages';
	const INTERIOR_LINES_IDENTIFIER = 'Interior_lines';

	if (config.displayOnlyPackageCat) {
		visiblePackages = visiblePackages?.filter(
			(item) => item.group.id.toUpperCase() === PACKAGE_IDENTIFIER.toUpperCase(),
		);
		features = features?.filter((item) => item.group.id.toUpperCase() === PACKAGE_IDENTIFIER.toUpperCase());
	} else {
		visiblePackages = visiblePackages?.filter(
			(item) => item.group.id.toUpperCase() !== INTERIOR_LINES_IDENTIFIER.toUpperCase(),
		);
		features = features?.filter((item) => item.group.id.toUpperCase() !== INTERIOR_LINES_IDENTIFIER.toUpperCase());
	}

	const packages = [...array(visiblePackages), ...array(features)];

	return packages?.map((item) => {
		return {
			...getDefaultProperties('package', {
				...item,
				name: item.name || '',
			}),
			...getPrices(item),
			teaserImageUrl:
				GetUrlFromImageSet(item.imageSets, 'teaser_image') ||
				GetUrlFromImageSet(item.imageSets, 'hd_thumbnail'),
			galleryImages: GetUrlArrayFromImageSet(item.imageSets, 'hd_image') || [''],
			galleryMedia: GetModifiedItemsWithItemNameFromMedia(
				GetMediaArrayFromImageSet(item.imageSets, 'hd_image') || [],
				item.name || '',
			),
			content: (item.__typename === 'ConfiguredCarPackage' && item.content) || [],
		};
	});
}

export type CatalogFeatures = ReturnType<typeof mapCatalog>;

function getDefaultProperties<
	const Typename extends FeatureCategory,
	T extends { pr7: string; status: Status; name: string | null | undefined },
>(typename: Typename, item: T): DefaultItemProperties<Typename, T> {
	return {
		type: typename,
		__item: item,
		pr7: item.pr7,
		featureId: item.pr7,
		status: item.status,
		name: item.name,
	};
}

type DefaultItemProperties<Typename, T> = {
	type: Typename;
	__item: T;
	featureId: string;
	pr7: string;
	name: string | null | undefined;
	status: Status;
};

export type CatalogItem = {
	type: FeatureCategory;
	featureId: string;
	pr7: string;
	name: string;
	status: Status;
	totalPrice: PriceFragment | undefined | null;
};

function getPrices(feature: ConfiguredCarFeatureFragment | ConfiguredCarPackageFragment) {
	return {
		totalPrice: feature.price as PriceFragment,
	};
}

/** Returns the given array or an empty array, if the given array is `null` or `undefined`. */
function array<T>(arr: T[] | undefined | null): T[] {
	return arr ?? [];
}
